import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatNumber, formatDate, formatDateTime } from '../utils/formatters';
import { Search, ChevronLeft, ChevronRight, Package, Eye, X, ArrowUpFromLine, Calendar } from 'lucide-react';

export default function StockOutPage() {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', type: '', date_from: '', date_to: '' });
  const [viewingMovement, setViewingMovement] = useState(null);

  useEffect(() => {
    fetchMovements();
  }, [pagination.page, filters]);

  const fetchMovements = async () => {
    setLoading(true);
    try {
      const params = queryParams({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        type: filters.type || 'out',
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      });
      const res = await api.get('/inventory/movements', { params });
      if (res.data.success) {
        setMovements(res.data.data?.movements || res.data.data?.items || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMovement = async (movement) => {
    try {
      const res = await api.get(`/inventory/movements/${movement.id}`);
      if (res.data.success) {
        setViewingMovement(res.data.data);
      }
    } catch (error) {
      // Fallback: show basic info
      setViewingMovement(movement);
    }
  };

  const typeBadge = (type) => {
    const map = {
      out: 'badge-danger',
      keluar: 'badge-danger',
      adjustment: 'badge-info',
      penyesuaian: 'badge-info',
      transfer_out: 'badge-warning',
      sales: 'badge-danger',
      sales_return_in: 'badge-success',
    };
    return map[type?.toLowerCase()] || 'badge-gray';
  };

  const typeLabel = (type) => {
    const map = {
      out: 'Keluar',
      keluar: 'Keluar',
      adjustment: 'Penyesuaian',
      penyesuaian: 'Penyesuaian',
      transfer_out: 'Transfer Keluar',
      sales: 'Penjualan',
      sales_return_in: 'Retur Penjualan',
    };
    return map[type?.toLowerCase()] || type;
  };

  const totalQty = movements.reduce((sum, m) => sum + Math.abs(m.quantity || 0), 0);
  const totalValue = movements.reduce((sum, m) => sum + (m.total_value || Math.abs(m.quantity || 0) * (m.unit_price || 0)), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Stok Keluar</h1>
          <p className="page-subtitle">Riwayat pergerakan stok keluar (penjualan, penyesuaian, dll)</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card">
          <div className="stat-icon stat-icon-danger">
            <ArrowUpFromLine className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Total Transaksi Keluar</p>
            <p className="stat-value">{formatNumber(movements.length)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon-warning">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Total Qty Keluar</p>
            <p className="stat-value">{formatNumber(totalQty)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon-neutral">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Total Nilai Keluar</p>
            <p className="stat-value">{formatCurrency(totalValue)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari produk, referensi..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchMovements()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                <option value="">Semua Tipe</option>
                <option value="out">Keluar</option>
                <option value="adjustment">Penyesuaian</option>
                <option value="transfer_out">Transfer Keluar</option>
              </select>
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                className="input pl-9"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                className="input pl-9"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={fetchMovements} className="btn-primary btn-sm">Cari</button>
            <button onClick={() => setFilters({ search: '', type: '', date_from: '', date_to: '' })} className="btn-secondary btn-sm">Reset</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-40" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : movements.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <ArrowUpFromLine className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data Stok Keluar</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada pergerakan stok keluar yang tercatat.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Produk</th>
                    <th>Tipe</th>
                    <th>Referensi</th>
                    <th>Gudang</th>
                    <th className="text-right">Jumlah</th>
                    <th className="text-right">Harga Satuan</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => (
                    <tr key={movement.id}>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(movement.date || movement.created_at)}</span>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{movement.product_name || movement.product?.name || '-'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{movement.sku || movement.product?.sku || ''}</p>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${typeBadge(movement.type || movement.movement_type)}`}>
                          {typeLabel(movement.type || movement.movement_type)}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{movement.reference || movement.doc_number || '-'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{movement.warehouse_name || movement.warehouse?.name || '-'}</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm font-medium text-danger-600">-{formatNumber(Math.abs(movement.quantity || 0))}</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(movement.unit_price || movement.price)}</span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewMovement(movement)}
                          className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded"
                          title="Lihat Detail"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Detail Modal */}
      {viewingMovement && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Pergerakan Stok</h3>
              <button onClick={() => setViewingMovement(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Tanggal</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDateTime(viewingMovement.date || viewingMovement.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Tipe</p>
                  <span className={`badge ${typeBadge(viewingMovement.type || viewingMovement.movement_type)}`}>
                    {typeLabel(viewingMovement.type || viewingMovement.movement_type)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Produk</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingMovement.product_name || viewingMovement.product?.name || '-'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">SKU: {viewingMovement.sku || viewingMovement.product?.sku || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Referensi</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingMovement.reference || viewingMovement.doc_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Gudang</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingMovement.warehouse_name || viewingMovement.warehouse?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Jumlah</p>
                  <p className="font-medium text-danger-600">-{formatNumber(Math.abs(viewingMovement.quantity || 0))}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Harga Satuan</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(viewingMovement.unit_price || viewingMovement.price)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Nilai</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(viewingMovement.total_value || Math.abs(viewingMovement.quantity || 0) * (viewingMovement.unit_price || viewingMovement.price || 0))}
                  </p>
                </div>
              </div>
              {viewingMovement.notes && (
                <div>
                  <p className="label">Catatan</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{viewingMovement.notes}</p>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <button onClick={() => setViewingMovement(null)} className="btn-secondary">Tutup</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
